import {useEffect, useState} from "react";
import {useLoaderData, useOutletContext} from "react-router-dom";
import {ActivityOutletContext, MatchParticipant} from "../../interface";
import {Button, Col, Input, message, Row, Select, Space, Table, TableColumnsType, Typography, Tag} from "antd";
import Highlighter from "react-highlight-words";
import {DeleteOutlined, EditOutlined} from "@ant-design/icons";
import EditParticipantModal from "./_editParticipantModal";
import DeleteParticipantModal from './_deleteParticipantModal'
import {getMatchActivityParticipants} from "../../apiBridge";


const { Link } = Typography;

const ActivityParticipants: React.FC = () => {

    const { categories, activity, reloadData } = useOutletContext() as ActivityOutletContext
    const {participants} = useLoaderData() as {"participants": MatchParticipant[]}

    const [messageApi, contextHolder] = message.useMessage();

    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [loadingState, setLoadingState] = useState(false);

    const [isReload, setIsReload] = useState(false);
    const [targetUser, setTargetUser] = useState<MatchParticipant | null>(null);
    const [nowCategory, setNowCategory] = useState<number>(0)

    const [originItems, setOriginItems] = useState(participants)
    const [items, setItems] = useState(participants)
    const [searchValue, setSearchValue] = useState<string>("");

    useEffect( () => {
        const reloadUsers = async() => {
            setLoadingState(true)
            const res = await getMatchActivityParticipants(activity.id);
            if (res) {
                const data = await res.json();
                if (data.error) {
                    messageApi.error(data.error);
                } else {
                    setOriginItems(data.participants)
                    setItems(data.participants);
                }
            }

            reloadData("participants")
            setLoadingState(false);
        }

        if (isReload) {
            setIsReload(false);
            reloadUsers()
        }
    }, [isReload, activity.id, messageApi, reloadData])


    const onUserSearch = (value :string) => {
        setSearchValue(value);
        doSearch(value, nowCategory)
    }

    const onCategoryChange = (value: number) => {
        setNowCategory(value);
        doSearch(searchValue, value)
    }

    const doSearch = (value: string, cat: number) => {
        let users = [];

        for (let i = 0; i < originItems.length; i++) {
            if (cat > 0) {
                if (!originItems[i].category_ids.includes(cat)) {
                    continue;
                }
            }

            if (originItems[i].display_name.toLowerCase().includes(value.toLowerCase()) ||
                originItems[i].license_number.toLowerCase().includes(value.toLowerCase()) ||
                (`${originItems[i].first_name} ${originItems[i].last_name}`.toLowerCase().includes(value.toLowerCase())) ||
                originItems[i].team_name.toLowerCase().includes(value.toLowerCase())) {
                users.push(originItems[i])
            }
        }

        setItems(users);
    }

    const editUser = (data: MatchParticipant) => {
        setTargetUser(data);
        setIsEditOpen(true)
    }

    const deleteUser = (data: MatchParticipant)=> {
        setTargetUser(data);
        setIsDeleteOpen(true);
    }

    const openCreate = () => {
        setTargetUser(null);
        setIsEditOpen(true)
    }

    const columns: TableColumnsType<MatchParticipant> = [
        {
            title: "Display Name",
            key: "display_name",
            dataIndex: "display_name",
            sorter: (a, b) => a.display_name.localeCompare(b.display_name),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Full Name",
            key: "full_name",
            render: (_, record) => {
                const fullName = `${record.first_name} ${record.last_name}`.trim();
                return (
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchValue]}
                        autoEscape
                        textToHighlight={fullName}
                    />
                );
            },
        },
        {
            title: "Gender",
            key: "gender",
            dataIndex: "gender",
            filters: [
                { text: 'Male', value: 'M' },
                { text: 'Female', value: 'F' }
            ],
            onFilter: (value, record) => record.gender === value,
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "License Number",
            key: "license_number",
            dataIndex: "license_number",
            sorter: (a, b) => a.license_number.localeCompare(b.license_number),
            render: (text) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchValue]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ),
        },
        {
            title: "Team",
            key: "team_name",
            dataIndex: "team_name",
            sorter: (a, b) => a.team_name.localeCompare(b.team_name),
            render: (text) => (
                <div style={{ 
                    maxWidth: '200px', 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word' 
                }}>
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchValue]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                </div>
            ),
        },
        {
            title: "Categories",
            key: "categories",
            render: (_, record) => {
                if (!categories) return null;
                return (
                    <Space wrap>
                        {record.category_ids.map(categoryId => {
                            const category = categories.find(c => String(c.id) === String(categoryId));
                            return category ? (
                                <Tag key={categoryId} color="blue">{category.name}</Tag>
                            ) : (
                                <Tag key={categoryId} color="default" >{categoryId}</Tag>
                            );
                        })}
                    </Space>
                );
            }
        },
        {
            title: "Action",
            render: (_, record) => {
                return <>
                    <Space size="middle">
                        <Link onClick={() => editUser(record)}>
                            <EditOutlined />
                        </Link>
                        <Link type="danger" onClick={() => deleteUser(record)}>
                            <DeleteOutlined />
                        </Link>
                    </Space>
                </>
            }
        }
    ];

    return <>
        {contextHolder}
        <EditParticipantModal
            isOpen={isEditOpen}
            controller={setIsEditOpen}
            categories={categories ? categories : []}
            activity={activity}
            participant={targetUser}
            reloadController={setIsReload}
            messageController={messageApi}
        />

        <DeleteParticipantModal
            isOpen={isDeleteOpen}
            controller={setIsDeleteOpen}
            activity={activity}
            participant={targetUser}
            reloadController={setIsReload}
            messageController={messageApi}
        />

        <div
            style={{
                background: "white",
                borderRadius: 4,
                boxShadow: "0 0 8px 0 #cbcbcb",
                padding: 20,
                marginBottom: 20
            }}
        >
            <Row gutter={12}>
                <Col sm={6}>
                    <Select style={{ width: '100%' }} defaultValue={0} onChange={onCategoryChange}>
                        <Select.Option key={0} value={0}>All</Select.Option>
                        {categories?.map((category) => (
                            <Select.Option key={category.id} value={category.id}>
                                {category.name}
                            </Select.Option>
                        )) || [
                            <Select.Option key="no-category" value="" disabled>
                                No Category Available
                            </Select.Option>
                        ]}
                    </Select>
                </Col>

                <Col sm={18}>
                    <Input.Search
                        placeholder="Search User"
                        allowClear
                        onChange={(e) => onUserSearch(e.target.value)}
                        onSearch={onUserSearch}
                        value={searchValue}
                    />
                </Col>
            </Row>



            <Space style={{marginTop: 20}}>
                <Button onClick={() => openCreate()}>Add Participant</Button>
            </Space>
        </div>


        <Table
            columns={columns}
            dataSource={items}
            loading={loadingState}
            rowKey="id"
            scroll={{
                x: "max-content",
            }}
        />
    </>
};

export default ActivityParticipants
